import { RefObject, useEffect, useRef } from 'react';
import { useColumnFilterRegistryContext } from '../context/useColumnFilterRegistryContext';
import { withOpenOnFocusSuppressed } from '../../utils/forms/openOnFocusSuppression';

type UseColumnFilterAutoFocusArgs = {
  // Wrapper around the overlay input; the input is queried from here.
  wrapperRef: RefObject<HTMLElement | null>;
  // The column's resolved filter definition; falsy while the overlay is still loading it (input not rendered yet).
  filterItem: unknown;
  columnName: string;
};

// Focuses the filter overlay value input on every open — shortcut or funnel click (JS-1230). Waits for the input to actually render (filter definition can load slowly) and injects the buffered chars.
export const useColumnFilterAutoFocus = ({
  wrapperRef,
  filterItem,
  columnName,
}: UseColumnFilterAutoFocusArgs) => {
  const { consumePendingFocus } = useColumnFilterRegistryContext();

  // Focus only once per open; the effect reruns whenever the filter definition identity changes and must not steal focus back from the user.
  const hasFocusedRef = useRef(false);

  useEffect(() => {
    // Filter definition not loaded yet, so the input isn't rendered; this effect reruns once it lands.
    if (!filterItem) return;
    if (hasFocusedRef.current) return;

    // Chars typed via the shortcut; empty for a mouse open, which then only focuses and preselects the value.
    const initialChar = consumePendingFocus(columnName) ?? '';

    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    let frame = 0;
    let observer: MutationObserver | null = null;

    const focusInput = () => {
      // ion-checkbox covers the boolean filter: its native input is inside a shadow root, but the host has tabindex=0 and toggles on Space by itself, so focusing the host is enough.
      const target = wrapper.querySelector(
        'input:not([type="hidden"]), textarea, ion-checkbox'
      ) as HTMLElement | null;
      if (!target) return false;

      hasFocusedRef.current = true;

      // Defer a frame so we focus after PrimeReact auto-focuses the match-mode select on open, not before (or it steals focus back).
      frame = window.requestAnimationFrame(() => {
        // Suppress open-on-focus side effects (reference quicksearch popover): the quick filter should focus the value only; the popover opens on the first keystroke or click.
        withOpenOnFocusSuppressed(() => target.focus());

        // ion-checkbox: focus is all it needs (Space toggles natively); no text to inject or select.
        if (
          !(target instanceof HTMLInputElement) &&
          !(target instanceof HTMLTextAreaElement)
        ) {
          return;
        }

        if (initialChar) {
          // Append (not overwrite) so reopening keeps the previous filter text.
          const nextValue = (target.value ?? '') + initialChar;
          // Set via the native setter + fire 'input' so React's controlled input registers the new value.
          const proto =
            target instanceof HTMLTextAreaElement
              ? HTMLTextAreaElement.prototype
              : HTMLInputElement.prototype;
          const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
          setter?.call(target, nextValue);
          target.dispatchEvent(new Event('input', { bubbles: true }));

          const end = target.value.length;
          target.setSelectionRange?.(end, end);
        } else {
          target.select?.();
        }

        // Bring a horizontally scrollable ancestor (e.g. chips container) to its end so the input/caret is visible. Space-trigger has no injected char to nudge scroll natively, and label-lookup chip resolution happens later — without this the input can sit off-screen behind existing chips.
        let scrollable: HTMLElement | null = target.parentElement;
        while (scrollable) {
          const style = window.getComputedStyle(scrollable);
          if (
            (style.overflowX === 'auto' || style.overflowX === 'scroll') &&
            scrollable.scrollWidth > scrollable.clientWidth
          ) {
            scrollable.scrollLeft = scrollable.scrollWidth;
            break;
          }
          scrollable = scrollable.parentElement;
        }
      });
      return true;
    };

    if (!focusInput()) {
      // Input not in the DOM yet (a nested async control); wait for it to mount with no time cap instead of polling.
      observer = new MutationObserver(() => {
        if (focusInput()) observer?.disconnect();
      });
      observer.observe(wrapper, { childList: true, subtree: true });
    }

    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [filterItem, columnName, consumePendingFocus, wrapperRef]);
};
